<template>
  <div class="dashboard-cards-flat">
    <div
      v-for="item in items"
      :key="item.label"
      class="dashboard-card-flat"
      :style="{ background: item.bg }"
      @click="showSidebar(item)"
      style="cursor: pointer"
    >
      <div class="dashboard-card-flat-content">
        <div class="dashboard-card-flat-icon">
          <i :class="item.icon"></i>
        </div>
        <div class="dashboard-card-flat-center">
          <div class="dashboard-card-flat-label">{{ item.label }}</div>
          <div class="dashboard-card-flat-value">{{ item.value }}</div>
        </div>
      </div>
    </div>
    <Sidebar
      v-model:visible="sidebarVisible"
      position="right"
      :header="sidebarTitle"
      :style="{ width: '900px', maxWidth: '98vw' }"
    >
      <div v-if="sidebarItem">
        <component
          :is="sidebarComponentName"
          :item="sidebarItem"
          :filter="props.filter"
        />
      </div>
    </Sidebar>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import Sidebar from "primevue/sidebar";
import BusesSidebarDetails from "./sidebar/BusesSidebarDetails.vue";
import CaptainsSidebarDetails from "./sidebar/CaptainsSidebarDetails.vue";
import RoutesSidebarDetails from "./sidebar/RoutesSidebarDetails.vue";
import RidesSidebarDetails from "./sidebar/RidesSidebarDetails.vue";
import BookingsSidebarDetails from "./sidebar/BookingsSidebarDetails.vue";

const props = defineProps({ filter: Object });
const { t } = useI18n();
const store = useStore();
const dashboardCounts = computed(
  () => store.getters["dashboard/getDashboardCounts"] || {}
);
const items = computed(() => [
  {
    label: t('dashboard.buses'),
    value: dashboardCounts.value.busesCount ?? "-",
    icon: "fa-solid fa-bus",
    bg: "#6366f1",
  },
  {
    label: t('dashboard.captains'),
    value: dashboardCounts.value.captainsCount ?? "-",
    icon: "fa-solid fa-user-tie",
    bg: "#10b981",
  },
  {
    label: t('dashboard.routes'),
    value: dashboardCounts.value.routesCount ?? "-",
    icon: "fa-solid fa-route",
    bg: "#fb923c",
  },
  {
    label: t('dashboard.rides'),
    value: dashboardCounts.value.ridesCount ?? "-",
    icon: "fa-solid fa-road",
    bg: "#a78bfa",
  },
  {
    label: t('dashboard.bookings'),
    value: dashboardCounts.value.bookingsCount ?? "-",
    icon: "fa-solid fa-ticket-alt",
    bg: "#2563eb",
  },
]);

const sidebarVisible = ref(false);
const sidebarItem = ref(null);
const sidebarTitle = computed(() =>
  sidebarItem.value ? sidebarItem.value.label + " " + t('dashboard.details') : t('dashboard.details')
);

const sidebarComponentName = computed(() => {
  if (!sidebarItem.value) return null;
  switch (sidebarItem.value.label) {
    case t('dashboard.buses'):
      return BusesSidebarDetails;
    case t('dashboard.captains'):
      return CaptainsSidebarDetails;
    case t('dashboard.routes'):
      return RoutesSidebarDetails;
    case t('dashboard.rides'):
      return RidesSidebarDetails;
    case t('dashboard.bookings'):
      return BookingsSidebarDetails;
    default:
      return null;
  }
});

function showSidebar(item) {
  sidebarItem.value = item;
  sidebarVisible.value = true;
}

function fetchDashboardCounts() {
  store.dispatch("dashboard/fetchDashboardCounts", props.filter);
}

watch(() => props.filter, fetchDashboardCounts, {
  immediate: true,
  deep: true,
});
</script>

<style scoped>
.dashboard-cards-flat {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 2rem;
  margin-top: 2rem;
}
.dashboard-card-flat {
  border-radius: 0.7rem;
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: box-shadow 0.15s, transform 0.15s;
  box-shadow: 0 2px 8px 0 rgba(44, 62, 80, 0.07);
  padding: 0.4rem 1.2rem 0.4rem 1.2rem;
}
.dashboard-card-flat:hover {
  box-shadow: 0 4px 16px 0 rgba(44, 62, 80, 0.15);
  transform: translateY(-2px) scale(1.03);
}
.dashboard-card-flat-content {
  display: flex;
  align-items: center;
  gap: 1.2rem;
  width: 100%;
  justify-content: flex-start;
}
.dashboard-card-flat-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
}
.dashboard-card-flat-label,
.dashboard-card-flat-value {
  text-align: center;
  width: 100%;
}
.dashboard-card-flat-icon {
  font-size: 2.5rem;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}
.dashboard-card-flat-label {
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}
.dashboard-card-flat-value {
  color: #fff;
  font-size: 1.5rem;
  font-weight: 700;
}
@media (max-width: 900px) {
  .dashboard-cards-flat {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
}
@media (max-width: 600px) {
  .dashboard-cards-flat {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}
</style>
